/**
 * §7.1 Hero — Enterprise Licensing hero.
 * Left: title + subtitle + CTAs.
 * Right: animated licence-to-live flow visual (License → Stripe →
 * Workspace → Your Logo → Your Domain → Platform Live).
 */

import React from 'react'
import {
  CreditCard, Calendar, ShieldCheck, Rocket, Sparkles, ArrowRight,
  Lock, ServerCog, Palette, Globe, CheckCircle2,
} from 'lucide-react'
import {
  Container, Eyebrow, GradientButton, GhostButton,
} from '@/components/landing/design-system'

const FLOW = [
  { label: 'License',   iconKey: 'CreditCard',   tone: '#FFC107' },
  { label: 'Stripe',    iconKey: 'Lock',         tone: '#3b82f6' },
  { label: 'Workspace', iconKey: 'ServerCog',    tone: '#22d3ee' },
  { label: 'Your Logo', iconKey: 'Palette',      tone: '#a855f7' },
  { label: 'Your Domain', iconKey: 'Globe',       tone: '#22c55e' },
  { label: 'Live',      iconKey: 'CheckCircle2', tone: '#FFC107' },
]
const FLOW_ICONS = { CreditCard, Lock, ServerCog, Palette, Globe, CheckCircle2 }

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(255,193,7,0.14), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />

      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Eyebrow>Pricing &amp; Licensing</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Enterprise licensing for{' '}
              <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">AI trading platform owners.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Launch your own AI trading SaaS business with a fully integrated enterprise
              platform — AI, Trading, White-Label, Academy, CRM, Analytics and Business Ops.
              <br />
              No infrastructure development. No engineering team. Launch under your own brand.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="#pricing-plans" size="lg">
                <CreditCard className="h-4 w-4" /> Purchase license
              </GradientButton>
              <GhostButton href="/contact" size="lg" trailingIcon>
                <Calendar className="h-4 w-4" /> Schedule enterprise demo
              </GhostButton>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { i: ShieldCheck, l: 'Enterprise-grade security' },
                { i: Rocket,       l: 'Launch in one afternoon' },
                { i: Sparkles,     l: 'All 12 modules included' },
              ].map((t) => (
                <span key={t.l} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <t.i className="h-3 w-3 text-brand-gold" /> {t.l}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — licence-to-live flow */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />

              {/* Header — 3 mini licence cards */}
              <div className="relative flex items-center justify-between gap-2 mb-4">
                {[
                  { l: 'Starter', p: '$25,000', a: false },
                  { l: 'Professional', p: '$40,000', a: true },
                  { l: 'Enterprise', p: '$60,000', a: false },
                ].map((c) => (
                  <div key={c.l}
                    className={`flex-1 rounded-lg border px-2.5 py-2 ${c.a ? 'border-brand-gold/40 bg-brand-gold/[0.08] shadow-[0_10px_30px_-10px_rgba(255,193,7,0.55)]' : 'border-white/10 bg-white/[0.02]'}`}>
                    <div className={`text-[9.5px] font-mono uppercase tracking-wider ${c.a ? 'text-brand-gold' : 'text-zinc-500'}`}>{c.l}</div>
                    <div className="text-[13px] font-semibold text-white">{c.p}</div>
                  </div>
                ))}
              </div>

              {/* Flow */}
              <div className="relative rounded-lg border border-white/10 bg-black/40 p-4">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">provisioning flow</div>
                <div className="grid grid-cols-6 gap-1.5">
                  {FLOW.map((n, i) => {
                    const Icon = FLOW_ICONS[n.iconKey] || Sparkles
                    return (
                      <div key={n.label} className="flex flex-col items-center gap-1.5">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border"
                          style={{ borderColor: `${n.tone}55`, background: `${n.tone}15`, color: n.tone }}>
                          <Icon strokeWidth={1.8} style={{ width: 15, height: 15 }} />
                        </span>
                        <span className="text-[9.5px] font-semibold text-zinc-300 text-center leading-tight">{n.label}</span>
                        <span className="text-[8px] font-mono text-zinc-600">{String(i + 1).padStart(2, '0')}</span>
                      </div>
                    )
                  })}
                </div>
                {/* animated packet */}
                <div className="relative mt-4 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="absolute inset-y-0 -left-1/4 w-1/4 bg-gradient-to-r from-transparent via-brand-gold to-transparent animate-brand-pkt" />
                </div>
              </div>

              {/* Success footer */}
              <div className="relative mt-3 flex items-center justify-between rounded-md border border-emerald-400/25 bg-emerald-400/[0.06] px-3 py-2">
                <div className="flex items-center gap-2 text-[10.5px] text-emerald-300">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  <span><span className="text-white font-semibold">Platform live</span> · app.alphatrade.io</span>
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-emerald-400 font-mono">
                  ~90s provisioning <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
